import { jsx as _jsx, jsxs as _jsxs, Fragment as _Fragment } from "react/jsx-runtime";
import { SpecraDataTable, SpecraOverlayDialog } from '@mono/ui-components/captains';
import { SpecraButton, SpecraLink, SpecraSurface, SpecraText } from '@mono/ui-components/ensigns';
import { SpecraInlineBanner, SpecraKeyValueRow, SpecraSectionHeader, SpecraStatusPill } from '@mono/ui-components/lieutenants';
import { Testids } from '@mono/ui-selectors';
import { useCallback, useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { useFeatureFlag } from '../../../../contexts/featureFlagContext';
import { requirementSource, useComplianceCoverage } from '../../../../hooks/useComplianceCoverage';
import { useOutlineSections } from '../../../../hooks/useOutline';
import { useProjectVolumes } from '../../../../hooks/useProjectVolumes';
import { useVolumeDocuments } from '../../../../hooks/useVolumeDocuments';
import { projectDataClient } from '../../../../lib/connect/clients';
import { ConnectWorkbookDialog } from './ConnectWorkbookDialog';
import { FillWorkbookDialog } from './FillWorkbookDialog';
import { parseWorkbookDiff, parseWorkbookMapping, usePriceDocumentMetadata, usePriceVolume } from './usePriceVolume';
import { usePricingWrite } from './usePricingRateBook';
import { WorkbookChangesPanel } from './WorkbookChangesPanel';
const T = Testids.pricingWorkspace;
export function PriceVolumeTab(props) {
    const enabled = useFeatureFlag('proposal_pricing_v2') === true;
    return enabled ? /*#__PURE__*/ _jsx(ProjectPriceVolume, {
        ...props
    }) : null;
}
function VolumeDocuments({ projectId, volumeId, onDocuments }) {
    const query = useVolumeDocuments(projectId, volumeId);
    // Keep the parent collection in sync with the shared per-volume query.
    return /*#__PURE__*/ _jsx(DocumentCollection, {
        query: query,
        volumeId: volumeId,
        onDocuments: onDocuments
    });
}
function DocumentCollection({ query, volumeId, onDocuments }) {
    useEffect(()=>{
        if (query.data) onDocuments(volumeId, query.data);
    }, [
        query.data,
        volumeId,
        onDocuments
    ]);
    if (query.isPending) return /*#__PURE__*/ _jsx(SpecraText, {
        children: "Loading volume documents…"
    });
    if (query.isError) return /*#__PURE__*/ _jsxs(SpecraInlineBanner, {
        tone: "danger",
        children: [
            "Volume documents unavailable.",
            ' ',
            /*#__PURE__*/ _jsx(SpecraButton, {
                onClick: ()=>query.refetch(),
                children: "Retry"
            })
        ]
    });
    return null;
}
function ProjectPriceVolume(props) {
    const volumes = useProjectVolumes(props.scope.projectId);
    const [byVolume, setByVolume] = useState({});
    const collect = useCallback((id, docs)=>setByVolume((old)=>old[id] === docs ? old : {
                ...old,
                [id]: docs
            }), []);
    const docs = (volumes.data || []).flatMap((v)=>byVolume[v.volumeId] || []).filter((d)=>d.documentType === 'xlsx');
    const ready = !!volumes.data && volumes.data.every((v)=>byVolume[v.volumeId]);
    return /*#__PURE__*/ _jsxs(_Fragment, {
        children: [
            volumes.isPending && /*#__PURE__*/ _jsx(SpecraText, {
                children: "Loading volumes…"
            }),
            volumes.isError && /*#__PURE__*/ _jsxs(SpecraInlineBanner, {
                tone: "danger",
                children: [
                    "Volumes unavailable. ",
                    /*#__PURE__*/ _jsx(SpecraButton, {
                        onClick: ()=>volumes.refetch(),
                        children: "Retry"
                    })
                ]
            }),
            volumes.data?.map((v)=>/*#__PURE__*/ _jsx(VolumeDocuments, {
                    projectId: props.scope.projectId,
                    volumeId: v.volumeId,
                    onDocuments: collect
                }, v.volumeId)),
            /*#__PURE__*/ _jsx(PriceVolumeContent, {
                ...props,
                documents: docs,
                documentsReady: ready,
                addSpreadsheetHref: `/proposal/${props.scope.projectId}/v2/${volumes.data?.[0] ? `volume/${volumes.data[0].volumeId}` : 'home'}`
            })
        ]
    });
}
function PriceVolumeContent({ scope, canEdit, categories, periods, documents, documentsReady, addSpreadsheetHref }) {
    const evidence = usePriceVolume(scope);
    const bindings = evidence.data?.workbooks.filter((w)=>w.bindingKind === 'volume_document' && !w.unboundAt) || [];
    const [dialog, setDialog] = useState();
    const [disconnectId, setDisconnectId] = useState('');
    const unbind = usePricingWrite(async (workbookId)=>projectDataClient.unbindPricingWorkbook({
            projectId: scope.projectId,
            workbookId
        }));
    if (evidence.isPending) return /*#__PURE__*/ _jsx(SpecraText, {
        children: "Loading price volume…"
    });
    if (evidence.isError) return /*#__PURE__*/ _jsxs(SpecraInlineBanner, {
        tone: "danger",
        children: [
            "Price volume unavailable: ",
            evidence.error.message,
            ' ',
            /*#__PURE__*/ _jsx(SpecraButton, {
                onClick: ()=>evidence.refetch(),
                children: "Retry"
            })
        ]
    });
    return /*#__PURE__*/ _jsxs("section", {
        "data-testid": T.priceVolumeTab,
        children: [
            /*#__PURE__*/ _jsx(SpecraSectionHeader, {
                title: "Price volume"
            }),
            !bindings.length && /*#__PURE__*/ _jsxs(SpecraSurface, {
                padding: "sm",
                children: [
                    /*#__PURE__*/ _jsx(SpecraStatusPill, {
                        tone: "neutral",
                        children: "Not connected"
                    }),
                    /*#__PURE__*/ _jsx(SpecraText, {
                        children: "Connect the government's spreadsheet to this scenario to review its category mapping."
                    }),
                    canEdit && /*#__PURE__*/ _jsx(SpecraButton, {
                        "data-testid": T.priceVolumeConnect,
                        disabled: !documentsReady,
                        onClick: ()=>setDialog('connect'),
                        children: "Connect workbook"
                    })
                ]
            }),
            bindings.map((binding)=>/*#__PURE__*/ _jsx(BoundWorkbook, {
                    scope,
                    canEdit,
                    categories,
                    periods,
                    documents,
                    binding,
                    addSpreadsheetHref,
                    onDisconnect: ()=>{
                        setDisconnectId(binding.workbookId);
                        setDialog('disconnect');
                    }
                }, binding.workbookId)),
            dialog === 'connect' && canEdit && /*#__PURE__*/ _jsx(ConnectWorkbookDialog, {
                scope,
                categories,
                periods,
                documents,
                addSpreadsheetHref,
                onClose: ()=>setDialog(undefined)
            }),
            dialog === 'disconnect' && canEdit && /*#__PURE__*/ _jsxs(SpecraOverlayDialog, {
                open: true,
                size: "sm",
                title: "Disconnect workbook?",
                onOpenChange: (open)=>{
                    if (!open && !unbind.isPending) setDialog(undefined);
                },
                footer: /*#__PURE__*/ _jsxs(_Fragment, {
                    children: [
                        /*#__PURE__*/ _jsx(SpecraButton, {
                            variant: "secondary",
                            disabled: unbind.isPending,
                            onClick: ()=>setDialog(undefined),
                            children: "Cancel"
                        }),
                        /*#__PURE__*/ _jsx(SpecraButton, {
                            "data-testid": T.priceVolumeDisconnectConfirm,
                            disabled: unbind.isPending,
                            onClick: async ()=>{
                                try {
                                    await unbind.mutateAsync(disconnectId);
                                    setDialog(undefined);
                                } catch  {
                                /* inline */ }
                            },
                            children: "Disconnect"
                        })
                    ]
                }),
                children: [
                    /*#__PURE__*/ _jsx(SpecraText, {
                        children: "This removes the pricing connection. The volume document stays in its volume."
                    }),
                    unbind.error && /*#__PURE__*/ _jsx(SpecraInlineBanner, {
                        tone: "danger",
                        children: unbind.error.message
                    })
                ]
            })
        ]
    });
}
function BoundWorkbook({ scope, canEdit, categories, periods, documents, binding, addSpreadsheetHref, onDisconnect }) {
    const doc = documents.find((d)=>d.volumeDocumentId === binding.volumeDocumentId);
    const metadata = usePriceDocumentMetadata(scope.projectId, doc, scope.principal);
    const evidence = usePriceVolume(scope);
    const [editing, setEditing] = useState(false);
    const [reviewing, setReviewing] = useState(false);
    const [filling, setFilling] = useState(false);
    const [filled, setFilled] = useState();
    let mapping;
    try {
        mapping = parseWorkbookMapping(binding.mappingJson);
    } catch  {
    /* Display an honest fallback for legacy data. */ }
    const proposals = (evidence.data?.proposals ?? []).filter((p)=>p.workbookId === binding.workbookId);
    const pending = proposals.filter((p)=>p.status === 'PENDING');
    const latest = [
        ...proposals
    ].sort((a, b)=>(b.createdAt?.toDate().getTime() ?? 0) - (a.createdAt?.toDate().getTime() ?? 0))[0];
    const latestDiff = latest && parseWorkbookDiff(latest.proposedDiffJson);
    const detectedVersion = latestDiff?.document_version_number || metadata.data?.versions.find((v)=>v.id === latest?.revisedWorkbookVersionId)?.versionNumber;
    const workbookHref = doc ? `/proposal/${scope.projectId}/v2/volume/${doc.volumeId}?doc=${doc.volumeDocumentId}` : undefined;
    const changes = pending.length > 0 || binding.reconciliationStatus !== 'MAPPED';
    const lastDocumentVersion = metadata.data?.versions.find((v)=>v.id === binding.lastFilledVersionId)?.versionNumber;
    const lastPricingVersion = evidence.data?.versions?.find((v)=>v.versionId === binding.lastFilledFromVersionId)?.versionNo;
    const filledDocumentVersion = metadata.data?.versions.find((v)=>v.id === filled?.documentVersionId)?.versionNumber;
    return /*#__PURE__*/ _jsxs(_Fragment, {
        children: [
            /*#__PURE__*/ _jsxs(SpecraSurface, {
                padding: "sm",
                "data-testid": T.priceVolumeBinding,
                children: [
                    filled && /*#__PURE__*/ _jsxs(SpecraInlineBanner, {
                        tone: filled.documentVersionId ? 'success' : 'info',
                        children: [
                            filled.documentVersionId ? `Filled ${filled.cellsWritten} cells. ${filledDocumentVersion ? `Document v${filledDocumentVersion}.` : 'Document version number unavailable.'}` : 'No cells written. No document version created.',
                            filled.skipped.length > 0 && ` Skipped ${filled.skipped.length} category/period entries.`
                        ]
                    }),
                    /*#__PURE__*/ _jsx(SpecraKeyValueRow, {
                        label: "Document",
                        value: doc?.label || doc?.submissionFilename || 'Spreadsheet metadata unavailable'
                    }),
                    /*#__PURE__*/ _jsx(SpecraKeyValueRow, {
                        label: "Status",
                        value: /*#__PURE__*/ _jsx(SpecraStatusPill, {
                            "data-testid": T.priceVolumeStatus,
                            tone: changes ? 'warning' : 'success',
                            children: changes ? pending.length > 0 || binding.reconciliationStatus === 'PENDING' ? 'Changes to review' : `Connection status: ${binding.reconciliationStatus || 'unavailable'}` : 'Connected'
                        })
                    }),
                    /*#__PURE__*/ _jsx(SpecraKeyValueRow, {
                        label: "Mapped rows",
                        value: mapping ? `${new Set(mapping.rows.map((r)=>r.category_id).filter(Boolean)).size} of ${categories.length} categories` : 'Mapping unavailable'
                    }),
                    /*#__PURE__*/ _jsx(SpecraKeyValueRow, {
                        label: "Original version",
                        value: metadata.data?.versions.find((v)=>v.id === binding.originalVersionId)?.versionNumber ? `v${metadata.data.versions.find((v)=>v.id === binding.originalVersionId)?.versionNumber}` : 'Version metadata unavailable'
                    }),
                    /*#__PURE__*/ _jsx(SpecraKeyValueRow, {
                        label: "Last filled",
                        value: binding.lastFilledVersionId ? lastDocumentVersion && lastPricingVersion ? `Last filled v${lastDocumentVersion} from pricing v${lastPricingVersion}` : 'Last filled: version metadata unavailable' : 'Not filled from pricing'
                    }),
                    /*#__PURE__*/ _jsx(SpecraKeyValueRow, {
                        label: "Last change detected",
                        value: latest ? `${latest.createdAt?.toDate().toLocaleString() || 'Detection time unavailable'}${detectedVersion ? ` · Price volume v${detectedVersion}` : ' · Document version metadata unavailable'}${latestDiff?.saving_user_name ? ` by ${latestDiff.saving_user_name}` : ''}` : binding.lastReadVersionId ? 'No changes detected' : 'Not checked for changes'
                    }),
                    doc && /*#__PURE__*/ _jsx(SpecraLink, {
                        "data-testid": T.priceVolumeOpen,
                        renderLink: ({ href, ...props })=>/*#__PURE__*/ _jsx(Link, {
                                ...props,
                                to: href
                            }),
                        href: `/proposal/${scope.projectId}/v2/volume/${doc.volumeId}?doc=${doc.volumeDocumentId}`,
                        children: "Open in ONLYOFFICE"
                    }),
                    pending.length > 0 && /*#__PURE__*/ _jsx(SpecraButton, {
                        "data-testid": T.priceVolumeReview,
                        onClick: ()=>setReviewing((open)=>!open),
                        children: reviewing ? 'Hide changes' : 'Review changes'
                    }),
                    canEdit && /*#__PURE__*/ _jsxs(_Fragment, {
                        children: [
                            /*#__PURE__*/ _jsx(SpecraButton, {
                                "data-testid": T.priceVolumeFill,
                                disabled: !doc || !mapping || changes,
                                onClick: ()=>{
                                    setFilled(undefined);
                                    setFilling(true);
                                },
                                children: "Fill from pricing"
                            }),
                            /*#__PURE__*/ _jsx(SpecraButton, {
                                "data-testid": T.priceVolumeChange,
                                disabled: !doc,
                                onClick: ()=>setEditing(true),
                                children: "Change mapping"
                            }),
                            /*#__PURE__*/ _jsx(SpecraButton, {
                                "data-testid": T.priceVolumeDisconnect,
                                onClick: onDisconnect,
                                children: "Disconnect"
                            })
                        ]
                    })
                ]
            }),
            /*#__PURE__*/ _jsx(SpecraInlineBanner, {
                tone: "info",
                children: "Anyone with the workbook open will be asked to reload."
            }),
            reviewing && pending.map((proposal)=>/*#__PURE__*/ _jsx(WorkbookChangesPanel, {
                    scope,
                    proposal,
                    categories,
                    periods,
                    workbookHref,
                    canEdit
                }, proposal.proposalId)),
            filling && canEdit && mapping && !changes && /*#__PURE__*/ _jsx(FillWorkbookDialog, {
                scope,
                categories,
                workbookId: binding.workbookId,
                periods: Array.from(new Set(mapping.rows.flatMap((r)=>Object.keys(r.cells ?? {})))).filter(Boolean).sort().map((periodKey)=>({
                        periodKey,
                        label: periods.find((p)=>p.periodKey === periodKey)?.label || periodKey
                    })),
                onClose: ()=>setFilling(false),
                onFilled: (result)=>{
                    setFilled(result);
                    setFilling(false);
                }
            }),
            doc ? /*#__PURE__*/ _jsx(PriceRequirements, {
                projectId: scope.projectId,
                doc: doc,
                sheet: mapping?.sheet || ''
            }) : /*#__PURE__*/ _jsx(SpecraText, {
                children: "Price-volume requirements need document metadata."
            }),
            editing && canEdit && /*#__PURE__*/ _jsx(ConnectWorkbookDialog, {
                scope,
                categories,
                periods,
                documents,
                addSpreadsheetHref,
                workbookId: binding.workbookId,
                initialDocumentId: binding.volumeDocumentId,
                onClose: ()=>setEditing(false)
            })
        ]
    });
}
function PriceRequirements({ projectId, doc, sheet }) {
    const coverage = useComplianceCoverage(projectId);
    const outline = useOutlineSections(doc ? projectId : '', doc?.volumeId);
    const sectionIds = new Set();
    const walk = (nodes)=>{
        for (const node of nodes){
            sectionIds.add(node.sectionId);
            walk(node.children || []);
        }
    };
    walk(outline.data || []);
    const attachment = `${doc?.submissionFilename || ''} ${doc?.label || ''} ${sheet}`.match(/attachment\s+j-?\d+/i)?.[0].toLowerCase();
    const rows = coverage.data?.items.filter((item)=>{
        if (item.superseded || item.versionId !== coverage.data.currentVersionId) return false;
        let text = '';
        try {
            const source = requirementSource(item);
            text = source.Text || source.SourceExactText || '';
        } catch  {
            return false;
        }
        return item.locations.some((l)=>l.kind === 'section' && sectionIds.has(l.id)) || !!attachment && text.toLowerCase().includes(attachment);
    }) || [];
    return /*#__PURE__*/ _jsxs(_Fragment, {
        children: [
            /*#__PURE__*/ _jsx(SpecraSectionHeader, {
                title: "Requirements for the price volume"
            }),
            coverage.isError || outline.isError ? /*#__PURE__*/ _jsxs(SpecraInlineBanner, {
                tone: "danger",
                children: [
                    "Requirements unavailable.",
                    ' ',
                    /*#__PURE__*/ _jsx(SpecraButton, {
                        onClick: ()=>{
                            coverage.refetch();
                            outline.refetch();
                        },
                        children: "Retry"
                    })
                ]
            }) : /*#__PURE__*/ _jsx(SpecraDataTable, {
                testId: T.priceVolumeRequirements,
                rows: rows,
                loading: coverage.isPending || outline.isPending,
                getRowId: (r)=>r.requirementId,
                emptyTitle: "No price-volume requirements found",
                columns: [
                    {
                        id: 'ref',
                        header: 'Ref',
                        cell: (r)=>requirementSource(r).SourceSectionNumber || 'Unspecified'
                    },
                    {
                        id: 'text',
                        header: 'Requirement',
                        cell: (r)=>requirementSource(r).Text || requirementSource(r).SourceExactText
                    },
                    {
                        id: 'state',
                        header: 'State',
                        cell: (r)=>/*#__PURE__*/ _jsx(SpecraStatusPill, {
                                tone: r.substantiveState === 'addressed' ? 'success' : 'warning',
                                children: r.substantiveState || 'Unaddressed'
                            })
                    },
                    {
                        id: 'link',
                        header: 'Compliance',
                        cell: ()=>/*#__PURE__*/ _jsx(SpecraLink, {
                                href: `/proposal/${projectId}/v2/compliance`,
                                children: "View compliance"
                            })
                    }
                ]
            })
        ]
    });
}
